import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, SupportedLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface SimpleLanguageSwitcherProps {
  className?: string;
  showLabel?: boolean;
}

/**
 * A simple language switcher that works without LanguageContext.
 * Use this on public pages (Home, Login, Register) that may render
 * before the full context provider hierarchy is available.
 */
export default function SimpleLanguageSwitcher({
  className,
  showLabel = true,
}: SimpleLanguageSwitcherProps) {
  const { i18n } = useTranslation();

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("preferredLanguage", lang);
  };

  return (
    <Select
      value={i18n.language as SupportedLanguage}
      onValueChange={handleLanguageChange}
    >
      <SelectTrigger
        className={cn(
          "[&>span]:flex h-9 [&>span]:items-center [&>span]:gap-2 w-auto min-w-[120px]",
          className,
        )}
      >
        <SelectValue placeholder="Language" />
      </SelectTrigger>
      <SelectContent>
        {SUPPORTED_LANGUAGES.map((ln) => (
          <SelectItem key={ln.code} value={ln.code}>
            <span className="flex items-center gap-2">
              <span className="text-lg leading-none">{ln.flag}</span>
              {showLabel && <span className="truncate">{ln.label}</span>}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
